/// <reference types="vite/client" />
/**
 * Factory function for creating isolated Zustand stores.
 *
 * Each call produces a new, independent store instance — NEVER a singleton.
 * Uses `createStore` from zustand/vanilla (Zustand 5 pattern).
 * In development, wraps with devtools middleware for Redux DevTools visibility.
 *
 * @internal
 * @module store/createMasterViewStore
 * @see FMPV-MVWC-E001-S003 — Store isolation requirement
 */
import { createStore } from 'zustand/vanilla';
import { devtools } from 'zustand/middleware';
import type { StateCreator } from 'zustand';
import type { MasterPatternViewEntity } from '../types/service.types';
import type { MasterPatternViewDefinition } from '../types/definition.types';
import type { MasterViewState, MasterViewStore } from './types';
import { createListSlice } from './slices/listSlice';
import { createFormSlice } from './slices/formSlice';
import { createFilterSlice } from './slices/filterSlice';
import { createUISlice } from './slices/uiSlice';
import { createOverrideSlice } from './slices/overrideSlice';

function buildStateCreator<T extends MasterPatternViewEntity>(
  definition: MasterPatternViewDefinition,
  defaultCreateValues?: Record<string, unknown>,
  initialPageSize?: number,
): StateCreator<MasterViewState<T>> {
  return (...args) => ({
    ...createListSlice<T>(initialPageSize)(...args),
    ...createFormSlice<T>(definition, defaultCreateValues)(...args),
    ...createFilterSlice<T>()(...args),
    ...createUISlice<T>()(...args),
    ...createOverrideSlice<T>(definition)(...args),
  });
}

/**
 * Creates a new isolated Zustand store instance for a MasterPatternView component.
 * In DEV mode, devtools middleware is applied for Redux DevTools visibility,
 * with each instance named after the definition name for easy identification.
 *
 * @param definition - The master view definition that configures initial state
 * @returns A new StoreApi instance with all slices combined
 */
export function createMasterViewStore<T extends MasterPatternViewEntity>(
  definition: MasterPatternViewDefinition,
  defaultCreateValues?: Record<string, unknown>,
  initialPageSize?: number,
): MasterViewStore<T> {
  const creator = buildStateCreator<T>(definition, defaultCreateValues, initialPageSize);

  if (import.meta.env.DEV) {
    return createStore<MasterViewState<T>>()(
      devtools(creator, { name: `MasterView-${definition.name}` }),
    );
  }

  return createStore<MasterViewState<T>>()(creator);
}
